import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { GAME_CONFIG, type Choice, type PublicPlayer, type RoomState } from '@majority-report/shared';
import { useGame } from '../net/GameProvider';
import { useCountdown } from '../hooks/useCountdown';
import { useQuestionText } from '../hooks/useQuestionText';
import { Hearts } from '../components/Hearts';
import { Banner, Button, Card, Pill, Row, Stack, Subtle } from '../components/ui';
import { pop, riseIn } from '../styles/GlobalStyle';

const Headline = styled.h2<{ $muted: boolean }>`
  font-size: clamp(1.3rem, 4.4vw, 2rem);
  font-weight: 800;
  text-align: center;
  line-height: 1.22;
  color: ${({ theme, $muted }) => ($muted ? theme.colors.inkSoft : theme.colors.ink)};
  animation: ${pop} 420ms ${({ theme }) => theme.transitions.bounce} both;
`;

/** Same responsive rule as the voting grid so the columns line up mentally. */
const Columns = styled.div`
  display: grid;
  gap: 14px;
  grid-template-columns: repeat(2, 1fr);

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const Column = styled.div<{ $side: Choice; $losing: boolean }>`
  padding: 18px 14px;
  border-radius: ${({ theme }) => theme.radii.lg};
  border: 3px solid transparent;
  transition: all 320ms ${({ theme }) => theme.transitions.bounce};

  ${({ theme, $side }) => {
    const palette = theme.choice[$side];
    return css`
      background: linear-gradient(165deg, ${palette.soft}, ${palette.base});
      color: ${palette.ink};
    `;
  }}

  ${({ $losing, theme }) =>
    $losing
      ? css`
          border-color: ${theme.colors.danger};
          box-shadow: 0 0 0 6px rgba(255, 107, 138, 0.22), ${theme.shadows.puffLg};
        `
      : css`
          box-shadow: ${theme.shadows.puff};
        `}
`;

const ColumnHead = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: center;
  text-align: center;
  margin-bottom: 14px;
`;

const OptionText = styled.div`
  font-weight: 800;
  font-size: 1rem;
  line-height: 1.28;
`;

const Count = styled.div`
  font-size: 2.1rem;
  font-weight: 800;
  line-height: 1;
`;

const Voters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  justify-content: center;
  min-height: 40px;
`;

const Voter = styled.div<{ $index: number; $hit: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  padding: 7px 9px;
  border-radius: ${({ theme }) => theme.radii.sm};
  background: rgba(255, 255, 255, 0.72);
  min-width: 62px;
  /* Stagger so the crowd pops in one by one instead of all at once. */
  animation: ${riseIn} 320ms ${({ theme }) => theme.transitions.bounce} both;
  animation-delay: ${({ $index }) => 90 + $index * 55}ms;

  ${({ $hit }) =>
    $hit &&
    css`
      box-shadow: 0 0 0 2.5px rgba(255, 107, 138, 0.7);
    `}
`;

const VoterFace = styled.div`
  font-size: 24px;
  line-height: 1;
`;

const VoterName = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  max-width: 66px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/** Ghost votes sit below the living ones, behind a divider and clearly labelled. */
const GhostSection = styled.div`
  margin-top: 12px;
  padding-top: 10px;
  border-top: 2px dashed rgba(255, 255, 255, 0.65);
`;

const GhostLabel = styled.div`
  font-size: 0.7rem;
  font-weight: 800;
  text-align: center;
  opacity: 0.7;
  margin-bottom: 7px;
`;

const Ghost = styled.div`
  animation: ${riseIn} 320ms ${({ theme }) => theme.transitions.bounce} both;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  padding: 5px 8px;
  border-radius: ${({ theme }) => theme.radii.sm};
  background: rgba(255, 255, 255, 0.4);
  min-width: 56px;
  /* Washed out and semi-transparent: present, but plainly not part of the count. */
  filter: grayscale(0.7);
  opacity: 0.75;
`;

const Knockouts = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Knockout = styled.div`
  animation: ${pop} 420ms ${({ theme }) => theme.transitions.bounce} both;
  padding: 12px 18px;
  border-radius: ${({ theme }) => theme.radii.md};
  background: linear-gradient(100deg, #ffe1e8, #fff0f4);
  color: #a03352;
  font-weight: 800;
  text-align: center;
`;

function VoterList({
  players,
  loserIds,
  maxHearts,
}: {
  players: PublicPlayer[];
  loserIds: string[];
  maxHearts: number;
}) {
  if (players.length === 0) return <Voters />;
  return (
    <Voters>
      {players.map((player, index) => {
        const hit = loserIds.includes(player.id);
        return (
          <Voter key={player.id} $index={index} $hit={hit}>
            <VoterFace aria-hidden="true">{player.avatar}</VoterFace>
            <VoterName>{player.nickname}</VoterName>
            <Hearts hearts={player.hearts} max={maxHearts} size={12} breaking={hit} />
          </Voter>
        );
      })}
    </Voters>
  );
}

export function RevealScreen({ state }: { state: RoomState }) {
  const { t } = useTranslation();
  const { actions, isHost } = useGame();
  const countdown = useCountdown(state.deadline, GAME_CONFIG.revealSeconds * 1000);
  const question = useQuestionText(state.question);

  const reveal = state.reveal;
  if (!reveal || !question) return null;

  // Everyone who was in the round — players eliminated *this* round are still
  // counted here, because their vote did count and seeing them go out is half
  // the fun of the reveal.
  const roundPlayers = state.players.filter((p) => p.alive || reveal.loserIds.includes(p.id));
  const votersFor = (choice: Choice) => roundPlayers.filter((p) => p.choice === choice);
  const silent = roundPlayers.filter((p) => reveal.silentIds.includes(p.id));

  // Ghosts: already out before this round, but they voted anyway. Shown apart
  // from the living so it stays obvious the column count excludes them.
  const ghosts = state.players.filter(
    (p) => !p.alive && !reveal.loserIds.includes(p.id) && p.choice != null,
  );
  const ghostsFor = (choice: Choice) => ghosts.filter((p) => p.choice === choice);

  const headline = (() => {
    switch (reveal.outcome) {
      case 'unanimous':
        return { text: `🧠 ${t('reveal.outcomeUnanimous')}`, muted: true };
      case 'tie':
        return { text: `⚖️ ${t('reveal.outcomeTie')}`, muted: true };
      case 'no-answers':
        return { text: `🦗 ${t('reveal.outcomeNoAnswers')}`, muted: true };
      case 'normal':
        return {
          text:
            state.settings.mode === 'minority-loses'
              ? `💔 ${t('reveal.outcomeNormalMinority')}`
              : `💔 ${t('reveal.outcomeNormalMajority')}`,
          muted: false,
        };
    }
  })();

  // The reveal is the last screen before the winners, so relabel the host's button.
  const survivors = state.players.filter((p) => p.alive).length;
  const isFinalReveal = survivors <= 2;

  return (
    <>
      <Row $justify="space-between" $wrap $gap="10px">
        <Row $gap="8px" $wrap>
          <Pill $tone="lavender">{t('question.roundLabel', { round: state.round })}</Pill>
          {question.authorName && (
            <Pill $tone="lemon">✏️ {t('question.byAuthor', { name: question.authorName })}</Pill>
          )}
        </Row>
        {state.deadline !== null && (
          <Pill $tone="mint">{t('reveal.nextIn', { seconds: countdown.seconds })}</Pill>
        )}
      </Row>

      <Card>
        <Stack $gap="20px">
          <Headline $muted={headline.muted}>{headline.text}</Headline>

          <Columns>
            {question.options.map(({ choice, text }) => {
              const losing = reveal.losingChoice === choice;
              return (
                <Column key={choice} $side={choice} $losing={losing}>
                  <ColumnHead>
                    <OptionText>{text}</OptionText>
                    <Count>{reveal.counts[choice]}</Count>
                    <Pill $tone={losing ? 'bubblegum' : 'mint'}>
                      {losing ? `💔 ${t('reveal.loses')}` : `💚 ${t('reveal.safe')}`}
                    </Pill>
                  </ColumnHead>
                  <VoterList players={votersFor(choice)} loserIds={reveal.loserIds} maxHearts={state.settings.startingHearts} />
                  {ghostsFor(choice).length > 0 && (
                    <GhostSection>
                      <GhostLabel>👻 {t('reveal.ghostVotes')}</GhostLabel>
                      <Voters>
                        {ghostsFor(choice).map((player) => (
                          <Ghost key={player.id}>
                            <VoterFace aria-hidden="true">{player.avatar}</VoterFace>
                            <VoterName>{player.nickname}</VoterName>
                          </Ghost>
                        ))}
                      </Voters>
                    </GhostSection>
                  )}
                </Column>
              );
            })}
          </Columns>

          {silent.length > 0 && (
            <Stack $gap="8px">
              <Subtle $center>
                🤐 {t('reveal.silent')} — {t('reveal.silentPenalty')}
              </Subtle>
              <VoterList players={silent} loserIds={reveal.loserIds} maxHearts={state.settings.startingHearts} />
            </Stack>
          )}

          {reveal.eliminatedIds.length > 0 && (
            <Knockouts>
              {reveal.eliminatedIds.map((id) => {
                const player = state.players.find((p) => p.id === id);
                if (!player) return null;
                return (
                  <Knockout key={id}>
                    💥 {t('reveal.eliminated', { name: `${player.avatar} ${player.nickname}` })}
                  </Knockout>
                );
              })}
            </Knockouts>
          )}
        </Stack>
      </Card>

      {isHost ? (
        <Button type="button" $tone="primary" $size="lg" $full onClick={actions.nextRound}>
          {isFinalReveal ? `🏆 ${t('reveal.finishButton')}` : `➡️ ${t('reveal.nextButton')}`}
        </Button>
      ) : (
        <Banner $tone="info">{t('reveal.nextIn', { seconds: countdown.seconds })}</Banner>
      )}
    </>
  );
}
